// watch/VideoCaptionsSection.tsx — Captions in your video's settings (1.27.60): see the tracks,
// remove one, or add a .srt/.vtt for a language (replacing that language's track). Changes take
// effect straight away. Automatic captions are offered in the composer, where the clip is.
import { useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, IconButton, MetaRow, useToast } from '@/ui';
import { captionsService } from '@/core/services/captionsService';
import { useCaptionTracks, invalidateCaptions } from '../../captions/useCaptions';
import {
  CAPTION_LANGUAGES,
  captionLanguageLabel,
  defaultCaptionLanguage,
} from '../../captions/languages';
import { CaptionFileError, readCaptionFile } from '../../captions/vtt';

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 4px;
  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font: 500 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
    color: ${({ theme }) => theme.color.textPrimary};
  }
  small {
    color: ${({ theme }) => theme.color.textMuted};
  }
`;

const Add = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  select {
    padding: 6px 8px;
    border-radius: ${({ theme }) => theme.radius.sm};
    border: 1px solid ${({ theme }) => theme.color.glassBorder};
    background: ${({ theme }) => theme.color.surface};
    color: ${({ theme }) => theme.color.textPrimary};
    font: 500 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  }
`;

const Note = styled.p`
  margin: 0;
  font: 400 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textMuted};
`;

export function VideoCaptionsSection({ videoId }: { videoId: string }) {
  const toast = useToast();
  const [version, setVersion] = useState(0);
  const tracks = useCaptionTracks(videoId, version);
  const [language, setLanguage] = useState(defaultCaptionLanguage);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  function refresh() {
    invalidateCaptions(videoId);
    setVersion((v) => v + 1);
  }

  async function add(file: File) {
    setBusy(true);
    try {
      const { vtt } = await readCaptionFile(file);
      await captionsService.upload(videoId, {
        language,
        label: captionLanguageLabel(language),
        source: 'uploaded',
        vtt,
      });
      toast('Captions added');
      refresh();
    } catch (e) {
      toast(e instanceof CaptionFileError ? e.message : 'Those captions couldn’t be added');
    } finally {
      setBusy(false);
    }
  }

  async function remove(lang: string) {
    try {
      await captionsService.remove(videoId, lang);
      toast('Captions removed');
      refresh();
    } catch {
      toast('Something went wrong');
    }
  }

  return (
    <>
      {tracks === null ? (
        <Note role="status">Loading captions…</Note>
      ) : tracks.length === 0 ? (
        <Note>No captions yet.</Note>
      ) : (
        <List aria-label="Caption tracks">
          {tracks.map((t) => (
            <li key={t.language}>
              <MetaRow>
                <span>{t.label}</span>
                <small>{t.source === 'auto' ? 'made automatically' : 'your file'}</small>
              </MetaRow>
              <IconButton
                icon="trash"
                size={16}
                label={`Remove the ${t.label} captions`}
                onClick={() => void remove(t.language)}
              />
            </li>
          ))}
        </List>
      )}
      <Add>
        <select
          aria-label="Language of the caption file"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
        >
          {CAPTION_LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>
              {l.label}
            </option>
          ))}
        </select>
        <input
          ref={fileRef}
          type="file"
          accept=".vtt,.srt,text/vtt"
          hidden
          aria-label="Caption file to add"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void add(file);
          }}
        />
        <Button
          variant="secondary"
          size="sm"
          type="button"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
        >
          Add a caption file
        </Button>
      </Add>
    </>
  );
}
